/**
 * Error handling utilities for components
 * Provides functions that integrate with the toast system
 */

import type { ApiError } from "@/client"
import { createAppError } from "@/utils/errorHandler"

/**
 * Extract user-friendly error message from API error
 * This function is safe to use in components without hooks
 */
export const getErrorMessage = (err: ApiError | any): string => {
  const appError = createAppError(err)
  return appError.message
}

/**
 * Legacy handleError function wrapper for backward compatibility
 * Returns an error message that can be used with toast hooks
 *
 * Usage in components:
 * const { showErrorToast } = useCustomToast()
 * const errorMessage = handleError(err)
 * showErrorToast(errorMessage)
 */
export const handleError = (err: ApiError | any): string => {
  return getErrorMessage(err)
}

/**
 * Create an error handler that automatically shows toast
 * This function returns a handler that can be used in mutation callbacks
 *
 * Usage:
 * const { showErrorToast } = useCustomToast()
 * const errorHandler = createErrorHandler(showErrorToast)
 *
 * mutation({
 *   onError: errorHandler
 * })
 */
export const createErrorHandler = (
  showErrorToast: (message: string) => void,
) => {
  return (err: ApiError | any) => {
    const message = getErrorMessage(err)
    showErrorToast(message)
  }
}
